import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { Mail } from "lucide-react";
import toast from "react-hot-toast";

import { invoicesApi } from "../../api";
import { QueryErrorBanner } from "../../components/feedback/QueryFeedback";
import { useAuth } from "../../context/AuthContext";
import type { InvoiceRecord, InvoiceSearchGroup } from "../../types";
import { formatINR } from "../../utils/format";
import { generateInvoicePdfBlob } from "../../utils/invoicePrintDocument";
import { EmailInvoiceModal } from "./EmailInvoiceModal";
import "./FinanceLayout.css";

const STATUS_PENDING = "Payment Pending";
const STATUS_RECEIVED = "Payment Received";

function paymentLabel(status?: string | null): typeof STATUS_PENDING | typeof STATUS_RECEIVED {
  const value = (status || "").toLowerCase();
  if (value.includes("received") || value.includes("paid")) return STATUS_RECEIVED;
  return STATUS_PENDING;
}

function consigneeLabel(invoice: InvoiceRecord): string {
  const name = invoice.vendor_name?.trim() || "Consignee";
  const number = invoice.invoice_number?.trim() || invoice.id.slice(0, 8);
  return `${name} — ${number}`;
}

function formatSentAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

const FinanceInvoicesPage: React.FC = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"date" | "status">("date");
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [emailTarget, setEmailTarget] = useState<InvoiceRecord | null>(null);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  const groupsQuery = useQuery<InvoiceSearchGroup[]>({
    queryKey: ["invoice-search", search, sort],
    queryFn: () => invoicesApi.search({ q: search || undefined, sort }, token!),
    enabled: Boolean(token),
  });

  const groups: InvoiceSearchGroup[] = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);
  const allInvoices: InvoiceRecord[] = useMemo(
    () => groups.flatMap((group) => group.invoices),
    [groups],
  );

  const statusMutation = useMutation({
    mutationFn: ({ invoiceId, status }: { invoiceId: string; status: string }) =>
      invoicesApi.updateStatus(invoiceId, status, token!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invoice-search"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not update status"),
  });

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDownload = async () => {
    const ids = selected.size > 0 ? Array.from(selected) : allInvoices.map((invoice) => invoice.id);
    if (ids.length === 0) {
      toast.error("No invoices to download");
      return;
    }
    setBulkDownloading(true);
    try {
      const selectedInvoices = allInvoices.filter((invoice) => ids.includes(invoice.id));
      for (const invoice of selectedInvoices) {
        if (invoice.pdf_url) continue;
        const blob = await generateInvoicePdfBlob(invoice);
        await invoicesApi.uploadPdf(invoice.id, blob, token!);
      }

      const result = await invoicesApi.bulkDownload(ids, token!);
      if (result instanceof Blob) {
        const url = URL.createObjectURL(result);
        const a = document.createElement("a");
        a.href = url;
        a.download = ids.length === 1 ? "invoice.pdf" : "invoices.zip";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Downloaded ${ids.length} invoice${ids.length === 1 ? "" : "s"}`);
      } else {
        toast.error("Some invoices still need a PDF generated — try again.");
      }
      await queryClient.invalidateQueries({ queryKey: ["invoice-search"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk download failed");
    } finally {
      setBulkDownloading(false);
    }
  };

  return (
    <div className="finance-invoices">
      <h2 className="finance-invoices__title">Group Invoices by Events</h2>

      {groupsQuery.isError ? (
        <QueryErrorBanner
          message="Unable to load invoices"
          onRetry={() => void groupsQuery.refetch()}
          retrying={groupsQuery.isFetching}
        />
      ) : null}

      <div className="finance-invoices__panel">
        <div className="finance-invoices__toolbar">
          <input
            type="search"
            className="eventos-input finance-invoices__search"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="finance-invoices__tool"
            onClick={() => setSort((current) => (current === "date" ? "status" : "date"))}
          >
            Sort · {sort === "date" ? "Date" : "Status"}
          </button>
          <button
            type="button"
            className="finance-invoices__tool"
            disabled={bulkDownloading || allInvoices.length === 0}
            onClick={() => void handleBulkDownload()}
          >
            {bulkDownloading ? "Preparing…" : "Download"}
          </button>
          <button
            type="button"
            className={`finance-invoices__tool${editing ? " is-active" : ""}`}
            onClick={() => setEditing((current) => !current)}
          >
            {editing ? "Done" : "Edit"}
          </button>
        </div>

        <div className="finance-invoices__groups">
          {groupsQuery.isPending ? (
            <p className="finance-invoices__empty">Loading invoices…</p>
          ) : groups.length === 0 ? (
            <p className="finance-invoices__empty">No invoices yet. Generate invoices from an event workspace.</p>
          ) : (
            groups.map((group) => (
              <section key={group.event_id} className="finance-invoices__group">
                <h3 className="finance-invoices__group-title">{group.event_name}</h3>
                <div className="finance-invoices__cards">
                  {group.invoices.map((invoice) => {
                    const status = paymentLabel(invoice.status);
                    const isReceived = status === STATUS_RECEIVED;
                    return (
                      <article key={invoice.id} className="finance-invoice-card">
                        <div className="finance-invoice-card__main">
                          <div className="finance-invoice-card__name">{consigneeLabel(invoice)}</div>
                          <div className="finance-invoice-card__amount">{formatINR(Number(invoice.total ?? invoice.amount ?? 0))}</div>
                        </div>
                        <div className="finance-invoice-card__side">
                          <div className="finance-invoice-card__email-row">
                            <button
                              type="button"
                              className="finance-invoice-card__email"
                              onClick={() => setEmailTarget(invoice)}
                            >
                              Email Invoice
                              {invoice.sent_at ? (
                                <span className="finance-invoice-card__tip" role="tooltip">
                                  <Mail size={12} strokeWidth={2.2} />
                                  <span>
                                    Email Sent
                                    <small>{formatSentAt(invoice.sent_at)}</small>
                                  </span>
                                </span>
                              ) : null}
                            </button>
                            <label className="finance-invoice-card__check">
                              <input
                                type="checkbox"
                                checked={selected.has(invoice.id)}
                                onChange={() => toggleSelected(invoice.id)}
                              />
                            </label>
                          </div>
                          {editing ? (
                            <button
                              type="button"
                              className={`finance-invoice-card__status${isReceived ? " is-received" : ""}`}
                              disabled={statusMutation.isPending}
                              onClick={() =>
                                statusMutation.mutate({
                                  invoiceId: invoice.id,
                                  status: isReceived ? STATUS_PENDING : STATUS_RECEIVED,
                                })
                              }
                            >
                              {status}
                            </button>
                          ) : (
                            <span className={`finance-invoice-card__status${isReceived ? " is-received" : ""}`}>
                              {status}
                            </span>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>
      </div>

      {emailTarget ? (
        <EmailInvoiceModal
          invoice={emailTarget}
          token={token}
          onClose={() => setEmailTarget(null)}
          onSent={() => void queryClient.invalidateQueries({ queryKey: ["invoice-search"] })}
        />
      ) : null}
    </div>
  );
};

export default FinanceInvoicesPage;
